import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield } from 'lucide-react'

const STEPS = [
  { label: 'Initialising security layer…' },
  { label: 'Activating AI agents…' },
  { label: 'Loading portfolio data…' },
  { label: 'Securing connection…' },
]

export default function SplashScreen() {
  const navigate = useNavigate()
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(id)
          setTimeout(() => navigate('/login'), 400)
          return 100
        }
        return p + 2
      })
    }, 50)
    return () => clearInterval(id)
  }, [navigate])

  const currentStep = Math.min(Math.floor(progress / 25), STEPS.length - 1)

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-base"
      style={{ background: 'linear-gradient(160deg, #020617 0%, #0f172a 60%, #1e1b4b 100%)' }}
      role="main"
      aria-label="AgentVault Capital loading"
    >
      <div className="w-full max-w-sm px-8 text-center">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-2xl bg-grad-blue flex items-center justify-center shadow-2xl">
            <Shield size={40} className="text-white" aria-hidden />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-text-primary mb-1 tracking-wide">AgentVault Capital</h1>
        <p className="text-text-secondary text-sm mb-10 tracking-widest uppercase">Private Fund Terminal</p>

        {/* Progress bar */}
        <div
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Loading progress"
          className="w-full h-1 bg-surface-card rounded-full overflow-hidden mb-8"
        >
          <div
            className="h-full bg-blue rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Steps */}
        <ul className="space-y-2" aria-live="polite">
          {STEPS.map((step, i) => {
            const done = i < currentStep
            const active = i === currentStep
            return (
              <li
                key={i}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all duration-300 ${
                  active  ? 'bg-blue/10 text-text-primary'
                  : done  ? 'text-text-secondary'
                  : 'text-text-muted opacity-50'
                }`}
              >
                <span
                  aria-hidden
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    done ? 'bg-emerald' : active ? 'bg-blue animate-pulse' : 'bg-text-muted'
                  }`}
                />
                {step.label}
                {done && (
                  <span className="ml-auto text-emerald text-xs font-mono" aria-label="complete">
                    ✓
                  </span>
                )}
              </li>
            )
          })}
        </ul>

        <div className="mt-10 flex justify-center gap-6 text-xs text-text-muted">
          <span>256-bit TLS</span>
          <span>·</span>
          <span>MPC-ready</span>
          <span>·</span>
          <span>WCAG 2.1 AA</span>
        </div>
      </div>
    </div>
  )
}
